import { Component, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Button, EmptyState } from '../ui/primitives'

interface Props {
  children: ReactNode
  /** Resets the caught error whenever this changes (e.g. the current route path). */
  resetKey: string
}

interface State {
  error: Error | null
}

/**
 * Catches render-time crashes below it and shows a recoverable error screen instead of a
 * blank page. React error boundaries only catch render/lifecycle errors, not ones inside
 * event handlers or async callbacks — that's fine here, those are already caught locally
 * by each screen's own mutation/query error handling.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-2xl px-4 py-10">
          <EmptyState
            icon={AlertTriangle}
            title="This page hit a problem"
            hint="Something went wrong loading this screen. Try again, or go back and pick something else."
          />
          <div className="mt-4 flex justify-center">
            <Button onClick={() => this.setState({ error: null })}>Try again</Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
